import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Alert, AlertTitle, Stack } from '@mui/material';
import type { Username } from '@weft/types';
import { ResetPasswordSchema, type ConfirmationCode } from '@weft/types';
import { isError, isNotNullOrUndefined } from '@weft/utils';
import { useConfirmPasswordReset } from 'apps/app/src/hooks/requests/use-confirm-password-reset';
import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { PasswordInput } from '../../../LoginPage/login-page-components/PasswordInput';
import { InfoBox } from '../../../users-components/InfoBox';
import { BackToLogin } from '../BackToLogin';

type ResetPasswordFormProps = {
  onNext: () => void;
  email: Username;
  verificationCode: ConfirmationCode;
};

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ email, verificationCode, onNext }) => {
  const methods = useForm({ resolver: zodResolver(ResetPasswordSchema) });
  const { handleSubmit, formState: { isSubmitting, errors, isSubmitSuccessful } } = methods;
  const resetPasswordMutation = useConfirmPasswordReset();

  const onSubmit = handleSubmit(async ({ password }) => {
    await resetPasswordMutation.mutateAsync({
      email,
      newPassword: password,
      verificationCode,
    }, { onSuccess: () => onNext() });
  },  
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <Stack
          spacing={4}
          sx={{ width: '456px', padding: 2 }}
        >
          <InfoBox
            title="Set New Password"
            description="must be at least 8 characters."
          />
          
          <PasswordInput
            label="New password"
            error={isNotNullOrUndefined(errors.password)}
            helperText={errors.password?.message?.toString()}
            {...methods.register('password')}
          />

          <PasswordInput
            label="Confirm Password"
            error={isNotNullOrUndefined(errors.confirmPassword)}
            helperText={errors.confirmPassword?.message?.toString()}
            {...methods.register('confirmPassword')}
          />
         
          <Button
            variant="contained"
            fullWidth
            type="submit"
            size="large"
          >
            {isSubmitting && !isSubmitSuccessful ? 'Resetting...' : 'Reset password'}
          </Button>

          {resetPasswordMutation.isError && (
            <Alert severity="error">
              <AlertTitle>Reset failed!</AlertTitle>
              {isError(resetPasswordMutation.error) ? resetPasswordMutation.error.message : 'Please ensure password and confirmation match.'}
            </Alert>
          )}

          <BackToLogin />
        </Stack>
      </form>
    </FormProvider>
  );
};